import { useState } from 'react'
import Styles from './Link.module.scss'
import { SearchField } from '../SearchField/SearchField'
import { useImagePickerContext } from '../ImagePickerContext'
import { Portal } from 'components/Portal/Portal'
import { ConfirmSelectionButton } from '../ConfirmSelectionButton'
import { LinkProps } from './Types'
import { EmptyState } from './EmptyState'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { SelectionCount } from '../SelectionCount'
import { DeselectAllButton } from '../DeselectAllButton'
import { useConfirm } from './useConfirm'
import { MediaFileThumbnail } from '../ImageThumbnail/MediaFileThumbnail'
import { Error } from 'components/ArticlePostPreview/PostPreviewBody/Error'
import { validateImageUrl } from './validators'
import { useDebouncedCallback } from '@react-hookz/web'
import cx from 'classnames'

export const Link = ({
  actionsContainerRef,
  confirmLabel = 'Add To Post',
  containerClassName,
  previewClassName,
}: LinkProps) => {
  const { setSelectedImages, selectedImages, toggleSelectedImage } = useImagePickerContext()
  const [link, setLink] = useState('')
  const [validationState, setValidationState] = useState<
    'empty' | 'invalid' | 'valid-url' | 'valid-image' | 'valid-base64'
  >('empty')
  const { isMobile } = useResponsiveDesign()
  const { confirm, isConfirming } = useConfirm()

  const debouncedValidation = useDebouncedCallback(
    (url: string) => {
      const result = validateImageUrl(url)
      setValidationState(result.state)

      if (result.state === 'valid-image' || result.state === 'valid-base64') {
        setSelectedImages([{ url: result.url }])
      } else {
        setSelectedImages([])
      }
    },
    [setSelectedImages],
    500,
  )

  const handleLinkChange = (newLink: string) => {
    setLink(newLink)
    debouncedValidation(newLink)
  }

  const renderContent = () => {
    switch (validationState) {
      case 'empty':
        return <EmptyState />
      case 'invalid':
        return <Error errorMessages={['Please enter a valid URL']} />
      case 'valid-url':
        return (
          <Error
            errorMessages={[
              'The URL must be a direct link to an image file (ending in .jpg, .png, etc) or a base64 encoded image. For example: https://example.com/image.jpg',
            ]}
          />
        )
      case 'valid-image':
      case 'valid-base64':
        return (
          <MediaFileThumbnail
            mediaFile={{ url: link }}
            onToggleSelect={toggleSelectedImage}
            isSelected={selectedImages.some((i) => i.url === link)}
            controls
          />
        )
      default:
        return null
    }
  }

  return (
    <div className={cx(Styles.link, containerClassName)} data-testid="link-provider">
      {actionsContainerRef && (
        <Portal container={actionsContainerRef}>
          {isMobile ? (
            <ConfirmSelectionButton
              onPress={confirm}
              isDisabled={selectedImages.length === 0 || isConfirming}
              confirmLabel={confirmLabel}
            />
          ) : (
            <>
              {selectedImages.length > 0 && (
                <>
                  <SelectionCount />
                  <DeselectAllButton />
                  <ConfirmSelectionButton onPress={confirm} isDisabled={isConfirming} />
                </>
              )}
            </>
          )}
        </Portal>
      )}
      <SearchField value={link} onChange={handleLinkChange} placeholder="https://" />

      <div className={cx(Styles.preview, previewClassName)}>{renderContent()}</div>
    </div>
  )
}
